import React, { useEffect } from 'react';
import styled from 'styled-components';

const Page = styled.main`
  background: #FFF5EF; /* soft pastry-cream backdrop */
  padding-top: 80px; /* account for navbar */
`;

const PageHeading = styled.h2`
  font-family: 'Great Vibes', 'Betrycia-Regular', cursive; /* match Indulgence script font */
  font-size: clamp(2.5rem, 5vw, 3.2rem);
  font-weight: 400;
  color: var(--primary-purple);
  text-align: center;
  letter-spacing: 0.5px;
  margin: 0 0 1rem;
`;

const SectionGrid = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem 3rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
  @media (max-width: 600px) {
    padding: 2rem 1rem;
    gap: 1.25rem;
  }
`;

const TextBlock = styled.article`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  padding: 2rem; /* equal distance on all sides */
  height: 440px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;

  @media (max-width: 768px) {
    height: 360px;
    padding: 1.25rem;
  }
  @media (max-width: 480px) {
    height: 340px;
  }
`;

const Title = styled.h2`
  color: var(--primary-purple);
  font-size: 1.6rem;
  margin: 0 0 0.5rem;
`;

const Accent = styled.div`
  width: 60px;
  height: 4px;
  background: var(--primary-yellow);
  border-radius: 4px;
  margin: 0.4rem 0 1rem;
`;

const Paragraph = styled.p`
  color: #2B1D24;
  font-size: clamp(0.94rem, 1.8vw, 1.02rem);
  line-height: 1.7;
  margin: 0 0 0.8rem;
`;

const ImageWrap = styled.figure`
  margin: 0;
  height: 440px;
  border-radius: 12px; /* match text box rounding */
  overflow: hidden; /* keep photo corners rounded */

  @media (max-width: 768px) {
    height: 360px;
  }
  @media (max-width: 480px) {
    height: 340px;
  }
`;

const Photo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
`;

const AboutUs: React.FC = () => {
  useEffect(() => {
    const elements = document.querySelectorAll('[data-reveal]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <Page>
      <PageHeading data-reveal="down">About Us</PageHeading>
      <SectionGrid>
        {/* Row 1: Image left, Text right */}
        <ImageWrap data-reveal="left">
          <Photo src="/Lotus_Sundae.jpg" alt="Signature sundaes by Flour & Fancy" />
        </ImageWrap>
        <TextBlock data-reveal="right">
          <Title>About Flour & Fancy</Title>
          <Accent />
          <Paragraph>
            Flour & Fancy is an artisan bakery making small‑batch cakes, pastries, donuts, brownies and cookies with premium ingredients.
          </Paragraph>
          <Paragraph>
            We blend classic techniques with modern flavors to deliver fresh, beautiful desserts for everyday joy and special moments.
          </Paragraph>
        </TextBlock>

        {/* Row 2: Text left, Image right */}
        <TextBlock data-reveal="left">
          <Title>Timeless Taste, Crafted with Care</Title>
          <Accent />
          <Paragraph>
            Every item is prepared with care, consistency and detail for balanced flavor and an elegant finish.
          </Paragraph>
          <Paragraph>
            From custom cakes to office pastry boxes, we promise quality, punctual delivery and warm service.
          </Paragraph>
        </TextBlock>
        <ImageWrap data-reveal="right">
          <Photo src="/pastries.jpg" alt="Assorted pastries and handcrafted desserts" />
        </ImageWrap>
      </SectionGrid>
    </Page>
  );
};

export default AboutUs;